import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

interface RespostaRapida {
  id: string;
  titulo: string;
  conteudo: string;
  atalho: string;
  tags: string[];
}

const RespostasRapidas: React.FC = () => {
  const [respostas, setRespostas] = useState<RespostaRapida[]>([]);
  const [filteredRespostas, setFilteredRespostas] = useState<RespostaRapida[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [novaResposta, setNovaResposta] = useState({
    titulo: '',
    conteudo: '',
    atalho: '',
    tags: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Dados de exemplo
  useEffect(() => {
    const exemploRespostas: RespostaRapida[] = [
      {
        id: '1',
        titulo: 'Saudação',
        conteudo: 'Olá! Como posso ajudar você hoje?',
        atalho: '/ola',
        tags: ['saudação', 'início']
      },
      {
        id: '2',
        titulo: 'Agradecimento',
        conteudo: 'Obrigado por entrar em contato conosco. Estamos à disposição para qualquer dúvida adicional.',
        atalho: '/obrigado',
        tags: ['agradecimento', 'encerramento']
      },
      {
        id: '3',
        titulo: 'Horário de Atendimento',
        conteudo: 'Nosso horário de atendimento é de segunda a sexta, das 8h às 18h.',
        atalho: '/horario',
        tags: ['informação', 'horário']
      },
      {
        id: '4',
        titulo: 'Transferência',
        conteudo: 'Vou transferir seu atendimento para um especialista que poderá ajudar melhor com sua questão.',
        atalho: '/transferir',
        tags: ['transferência', 'encaminhamento']
      },
      {
        id: '5',
        titulo: 'Prazo de Entrega',
        conteudo: 'O prazo de entrega é de 3 a 5 dias úteis após a confirmação do pagamento.',
        atalho: '/prazo',
        tags: ['vendas', 'entrega']
      }
    ];

    setRespostas(exemploRespostas);
    setFilteredRespostas(exemploRespostas);
  }, []);

  // Filtrar respostas com base na pesquisa e na aba ativa
  useEffect(() => {
    let filtered = respostas;

    // Filtrar por tag
    if (activeTab !== 'todas') {
      filtered = filtered.filter((resposta) => 
        resposta.tags.includes(activeTab)
      );
    }

    // Filtrar por termo de pesquisa
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (resposta) =>
          resposta.titulo.toLowerCase().includes(term) ||
          resposta.conteudo.toLowerCase().includes(term) ||
          resposta.atalho.toLowerCase().includes(term) ||
          resposta.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }

    setFilteredRespostas(filtered);
  }, [respostas, searchTerm, activeTab]);

  // Obter todas as tags únicas
  const allTags = [...new Set(respostas.flatMap(r => r.tags))];

  // Adicionar nova resposta rápida
  const handleAddResposta = () => {
    const newId = (respostas.length + 1).toString();
    const tagsArray = novaResposta.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    const novaRespostaObj: RespostaRapida = {
      id: newId,
      titulo: novaResposta.titulo,
      conteudo: novaResposta.conteudo,
      atalho: novaResposta.atalho,
      tags: tagsArray
    };
    
    setRespostas([...respostas, novaRespostaObj]);
    setNovaResposta({
      titulo: '',
      conteudo: '',
      atalho: '',
      tags: ''
    });
    setIsDialogOpen(false);
  };

  // Excluir resposta rápida
  const handleDeleteResposta = (id: string) => {
    setRespostas(respostas.filter(resposta => resposta.id !== id));
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Respostas Rápidas</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Nova Resposta Rápida
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Adicionar Resposta Rápida</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="titulo">Título</Label>
                <Input
                  id="titulo"
                  value={novaResposta.titulo}
                  onChange={(e) => setNovaResposta({...novaResposta, titulo: e.target.value})}
                  placeholder="Ex: Saudação"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conteudo">Conteúdo</Label>
                <Textarea
                  id="conteudo"
                  value={novaResposta.conteudo}
                  onChange={(e) => setNovaResposta({...novaResposta, conteudo: e.target.value})}
                  placeholder="Ex: Olá! Como posso ajudar você hoje?"
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="atalho">Atalho</Label>
                <Input
                  id="atalho"
                  value={novaResposta.atalho}
                  onChange={(e) => setNovaResposta({...novaResposta, atalho: e.target.value})}
                  placeholder="Ex: /ola"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
                <Input
                  id="tags"
                  value={novaResposta.tags}
                  onChange={(e) => setNovaResposta({...novaResposta, tags: e.target.value})}
                  placeholder="Ex: saudação, início"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleAddResposta}>Adicionar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <Input
          type="text"
          placeholder="Pesquisar respostas rápidas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Tabs defaultValue="todas" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 flex flex-wrap">
          <TabsTrigger value="todas">Todas</TabsTrigger>
          {allTags.map((tag) => (
            <TabsTrigger key={tag} value={tag}>{tag}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredRespostas.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma resposta rápida encontrada.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRespostas.map((resposta) => (
                <Card key={resposta.id} className="overflow-hidden">
                  <CardHeader className="bg-gray-50 pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{resposta.titulo}</CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-red-500"
                        onClick={() => handleDeleteResposta(resposta.id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                      </Button>
                    </div>
                    <div className="text-sm text-gray-500">Atalho: <code className="bg-gray-100 px-1 rounded">{resposta.atalho}</code></div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 mb-3">{resposta.conteudo}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {resposta.tags.map((tag) => (
                        <span key={tag} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RespostasRapidas;
